import _slicedToArray from "/builds/isonishi/blog/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React from 'react';
import { useReducer, useMemo, useContext, useEffect } from 'react';
var isAtom = function isAtom(recoilValue) {
  return Object.keys(recoilValue).includes("default");
};
var coilStores = {};
var getCoilStore = function getCoilStore(coilId) {
  coilStores[coilId] = coilStores[coilId] || {};
  return coilStores[coilId];
};
var currentCoilId = 0;
var generateCoilId = function generateCoilId() {
  return (currentCoilId + 1).toString();
};
var coreGetAtomValue = function coreGetAtomValue(coilId, atom) {
  var coreCoilValue = getCoilStore(coilId)[atom.key];
  if (coreCoilValue.type !== "atom") {
    throw new Error("".concat(coreCoilValue.key, " is not an atom"));
  }
  return coreCoilValue.value;
};
var coreGetSelectorValue = function coreGetSelectorValue(coilId, selector) {
  return selector.get({
    get: createPublicGetCoilValue(coilId)
  });
};
var coreGetCoilValue = function coreGetCoilValue(coilId, coilValue) {
  return isAtom(coilValue) ? coreGetAtomValue(coilId, coilValue) : coreGetSelectorValue(coilId, coilValue);
};
var createPublicGetCoilValue = function createPublicGetCoilValue(coilId) {
  return function (coilValue) {
    return coreGetCoilValue(coilId, coilValue);
  };
};
var createPublicSetAtomValue = function createPublicSetAtomValue(coilId, coilValue) {
  return function (nextValue) {
    return coreSetAtomValue(coilId, coilValue, nextValue);
  };
};
var coreSetAtomValue = function coreSetAtomValue(coilId, coilValue, nextValue) {
  var coreCoilValue = getCoilStore(coilId)[coilValue.key];
  if (coreCoilValue.type !== "atom") {
    throw new Error("".concat(coreCoilValue.key, " is not an atom"));
  }
  if (nextValue !== coreCoilValue.value) {
    coreCoilValue.value = nextValue;
    coreCoilValue.subscribers.forEach(function (callback) {
      return callback();
    });
  }
};
var createPublicSetCoilValue = function createPublicSetCoilValue(coilId) {
  return function (coilValue, nextValue) {
    return coreSetCoilValue(coilId, coilValue, nextValue);
  };
};
var coreSetCoilValue = function coreSetCoilValue(coilId, coilValue, nextValue) {
  if (isAtom(coilValue)) {
    coreSetAtomValue(coilId, coilValue, nextValue);
  } else if (coilValue.set) {
    coilValue.set({
      get: createPublicGetCoilValue(coilId),
      set: createPublicSetCoilValue(coilId)
    }, nextValue);
  }
};
var registerCoilValue = function registerCoilValue(coilId, coilValue) {
  var key = coilValue.key;
  var coilStore = getCoilStore(coilId);
  if (coilStore[key]) {
    return;
  }
  if (isAtom(coilValue)) {
    coilStore[key] = {
      default: coilValue.default,
      key: key,
      subscribers: [],
      type: "atom",
      value: coilValue.default
    };
  } else {
    coilStore[key] = {
      key: key,
      subscribers: [],
      type: "selector"
    };
  }
};
var subscribeToCoilValueUpdates = function subscribeToCoilValueUpdates(coilId, key, callback) {
  var coilValue = getCoilStore(coilId)[key];
  var subscribers = coilValue.subscribers;
  if (subscribers.includes(callback)) {
    throw new Error("Already subscribed to Coil Value");
  }
  subscribers.push(callback);
  return function () {
    subscribers.splice(subscribers.indexOf(callback), 1);
  };
};
var CoilContext = React.createContext("");
var CoilProvider = function CoilProvider(props) {
  var children = props.children;
  var id = generateCoilId();
  return React.createElement(CoilContext.Provider, {
    value: id
  }, children);
};
var atom = function atom(a) {
  return a;
};
var selector = function selector(s) {
  return s;
};
var useCoilId = function useCoilId() {
  var coilId = useContext(CoilContext);
  if (!coilId) {
    throw new Error("Wrap your app with <CoilRoot>");
  }
  return coilId;
};
var createDependenciesSpy = function createDependenciesSpy(coilId, dependencies) {
  var dependenciesSpy = function dependenciesSpy(coilValue) {
    dependencies.push(coilValue.key);
    if (isAtom(coilValue)) {
      return coreGetCoilValue(coilId, coilValue);
    }
    return coilValue.get({
      get: dependenciesSpy
    });
  };
  return dependenciesSpy;
};
var useSubscribeToCoilValues = function useSubscribeToCoilValues(coilValue, callback) {
  var coilId = useCoilId();
  useEffect(function () {
    if (isAtom(coilValue)) {
      return subscribeToCoilValueUpdates(coilId, coilValue.key, callback);
    }
    var dependencies = [];
    coilValue.get({
      get: createDependenciesSpy(coilId, dependencies)
    });
    var unsubscribes = [];
    dependencies.forEach(function (key) {
      return unsubscribes.push(subscribeToCoilValueUpdates(coilId, key, callback));
    });
    return function () {
      return unsubscribes.forEach(function (unsubscribe) {
        return unsubscribe();
      });
    };
  }, [coilId, coilValue, callback]);
};
var useCoilValue = function useCoilValue(coilValue) {
  var coilId = useCoilId();
  var _useReducer = useReducer(function (s) {
      return s + 1;
    }, 0),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    forceRender = _useReducer2[1];
  registerCoilValue(coilId, coilValue);
  useSubscribeToCoilValues(coilValue, forceRender);
  return coreGetCoilValue(coilId, coilValue);
};
var useCoilState = function useCoilState(coilValue) {
  var coilId = useCoilId();
  var currentValue = useCoilValue(coilValue);
  var setter = useMemo(function () {
    if (isAtom(coilValue)) {
      return createPublicSetAtomValue(coilId, coilValue);
    }
    return function (nextValue) {
      if (coilValue.set) coilValue.set({
        get: createPublicGetCoilValue(coilId),
        set: createPublicSetCoilValue(coilId)
      }, nextValue);
    };
  }, [coilId, coilValue]);
  return [currentValue, setter];
};
export { CoilContext, CoilProvider, atom, isAtom, selector, useCoilState, useCoilValue };